import { Injectable } from "@angular/core";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

// multi-asset
import * as CubiqTS from "shared/cubiq-model-multi-asset";
// order-entry
import { OEModelMasterService } from "./oe-model-master.service";
import { OENOperationMode } from "../../data/enums/oe-operation-mode.enum";
import { OEModelGetterService } from "./oe-model-getter.service";
import { OENDisplayState } from "../../data/enums/oe-display-state.enum";
import {
  ICAutocompleteGenericModel,
  ICFixedCcyModel,
  ICCommentsModel,
  ICSuspendModel,
  ICTimeControlModel,
  ICTenorValueDateModel,
  ICFixingSourceModel,
  ICMarkupModel,
} from "../../interface/controls/models/bfx-interface-controls.models";
import { OEUtilityService } from "../basic/oe-utility.service";
import { ICGoodFromUntilModel } from "../../interface/controls/models/bfx-good-from-until.model";
import { OESettingsService } from "../basic/oe-settings.service";
import { OEDataModel } from "../../data/models/oe-data.model";
import { OEModelSyncService } from "./oe-model-sync.service";
import { OENEntryType } from "../../data/enums/oe-entry-type.enum";
import { ATDLParameterDataModel } from "../../data-atdl/model/atdl-parameter-data.model";
import { OEPanelGammaViewModel } from "app/order-entry/data/models/oe-panel-gamma-view.model";
import { ConstraintValue } from "../../../../shared/cubiq-services/model/core/ConstraintValue";

@Injectable()
export class OEModelSetterService {
  /**********************************************************************/
  /* CONSTRUCTOR */
  constructor(
    private master: OEModelMasterService,
    private getter: OEModelGetterService,
    private utility: OEUtilityService,
    private settings: OESettingsService,
    private modelSync: OEModelSyncService,
  ) {}

  /**********************************************************************/
  /*  MASTER API  */
  public set(refId: string, model: OEDataModel): void {
    this.master.set(refId, model);
  }

  public delete(refId: string): void {
    this.master.delete(refId);
  }

  public update(refId: string, model: OEDataModel): void {
    this.master.update(refId, model);
  }

  /**********************************************************************/
  /*  orderId */

  public updateOrderId(refId: string, orderId: string | CubiqTS.Long, orderRef: string): void {
    if (orderId instanceof CubiqTS.Long) {
      orderId = orderId.toString();
    }

    if (this.master.has(refId) && orderId != null && orderId !== "") {
      if (this.master.get(refId).orderId == null || this.master.get(refId).orderId === "") {
        let model: OEDataModel = this.master.get(refId);
        model.orderId = orderId;
        if (orderRef != null) {
          model.orderRef = orderRef;
        }
      }
    }
  }

  public setOrderId(refId: string, orderId: string | CubiqTS.Long): void {
    if (orderId instanceof CubiqTS.Long) {
      orderId = orderId.toString();
    }

    if (this.master.has(refId)) {
      this.master.get(refId).orderId = orderId;
    }
  }

  /**********************************************************************/
  /* Action issuer id  */

  public resetModifyIssuerOrderId(refId: string): void {
    this.setModifyIssuerOrderId(refId, null);
  }

  public setModifyIssuerOrderId(refId: string, orderId: string): void {
    if (this.master.has(refId)) {
      this.master.get(refId).modifyIssuedOrderId = orderId;
      if (this.master.hasStrategy(refId)) {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        propagationTree.forEach((itemRefId: string) => {
          this.master.get(itemRefId).modifyIssuedOrderId = orderId;
        });
      }
    }
  }

  /**********************************************************************/
  /*  Lifespan - initialization  */

  public startInInitialization(refId: string, applyOnAllLegs: boolean = true): void {
    if (this.master.has(refId)) {
      this.master.get(refId).isInInitialization = true;
      if (applyOnAllLegs && this.master.hasStrategy(refId)) {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        propagationTree.forEach((itemRefId: string) => {
          this.master.get(itemRefId).isInInitialization = true;
        });
      }
    }
  }

  public stopInInitialization(refId: string, applyOnAllLegs: boolean = true): void {
    if (this.master.has(refId)) {
      this.master.get(refId).isInInitialization = false;
      if (applyOnAllLegs && this.master.hasStrategy(refId)) {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        propagationTree.forEach((itemRefId: string) => {
          this.master.get(itemRefId).isInInitialization = false;
        });
      }
    }
  }

  /**********************************************************************/
  /* Request Model  */
  public setRequestModel(refId: string, requestModel: any): void {
    if (this.master.has(refId)) {
      let rootRefId = this.getter.getRootIdForNodeWithRefId(refId);
      if (refId === rootRefId || this.master.has(rootRefId)) {
        this.master.get(rootRefId).requestModel = requestModel;
      }
    }
  }

  public resetRequestModel(refId: string): void {
    if (this.master.has(refId)) {
      this.master.get(refId).requestModel = null;
    }
  }

  public setModifyInfoRequestModel(refId: string, requestModifyInfoModel: any): void {
    if (this.master.has(refId)) {
      let rootRefId = this.getter.getRootIdForNodeWithRefId(refId);
      if (refId === rootRefId || this.master.has(rootRefId)) {
        this.master.get(rootRefId).requestModifyInfoModel = requestModifyInfoModel;
      }
    }
  }

  public resetModifyInfoRequestModel(refId: string): void {
    if (this.master.has(refId)) {
      this.master.get(refId).requestModifyInfoModel = null;
    }
  }

  /**********************************************************************/
  /* Subscription Life-Cycle, Intervention & Scratchpad */
  public setLifeCycleSubscriptionStatus(refId: string, status: boolean): void {
    if (this.master.has(refId)) {
      this.master.get(refId).subscriptionLifeCycleActive = status;
    } else {
      throw new Error('Model ref: "' + refId + '" not found!');
    }
  }

  public setInterventionSubscriptionStatus(refId: string, status: boolean): void {
    if (this.master.has(refId)) {
      this.master.get(refId).subscriptionInterventionActive = status;
    } else {
      throw new Error('Model ref: "' + refId + '" not found!');
    }
  }

  public setScratchpadSubscriptionStatus(refId: string, status: boolean): void {
    if (this.master.has(refId)) {
      this.master.get(refId).subscriptionScratchpadActive = status;
    } else {
      throw new Error('Model ref: "' + refId + '" not found!');
    }
  }

  /**********************************************************************/
  /* Display state */
  public setOrderDisplayState(refId: string, _state: OENDisplayState): void {
    if (this.master.has(refId) && _state != null) {
      this.master.get(refId).displayState = _state;
      if (this.master.hasStrategy(refId)) {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        propagationTree.forEach((itemRefId: string) => {
          if (this.master.get(itemRefId).displayState !== _state) {
            this.master.get(itemRefId).displayState = _state;
          }
        });
      }
    }
  }

  /**********************************************************************/
  /* Actively Displayed */
  public setActivelyDisplayed(refId: string): void {
    if (this.master.has(refId)) {
      logger.out.info(`oms-s - setting activelyDisplayed to ${refId}`);
      if (this.getter.isRootNode(refId) && this.getter.isRootNodeAlone(refId)) {
        this.master.get(refId).isActivelyDisplayed = true;
      } else if (this.master.hasStrategy(refId)) {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        propagationTree.forEach((itemRefId: string) => {
          if (itemRefId === refId) {
            this.master.get(itemRefId).isActivelyDisplayed = true;
          } else {
            this.master.get(itemRefId).isActivelyDisplayed = false;
          }
        });
      }
    }
  }

  /**********************************************************************/
  /* Server Order state */

  public setSrvOrderState(
    refId: string,
    _state: CubiqTS.OrderState,
    applyOnAllLegs: boolean = false,
    txTime: CubiqTS.Moment = null,
  ): void {
    if (this.master.has(refId)) {
      let model: any = this.master.get(refId);

      model.srvOrderState = _state;

      // We don't want this as part of the model at this time and overwriting with null is fine too
      model._srvOrderStateTxTime = txTime;

      if (this.master.hasStrategy(refId) && applyOnAllLegs) {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        propagationTree.forEach((itemRefId: string) => {
          if (this.master.get(itemRefId).srvOrderState !== _state) {
            this.master.get(itemRefId).srvOrderState = _state;
          }
        });
      }
    }
  }

  /**********************************************************************/
  /* Filled State */

  public resetFillState(refId: string): void {
    if (this.master.has(refId)) {
      this.master.get(refId).fillState = null;
      this.master.get(refId).hasStrategyFill = false;
    }
  }

  /**********************************************************************/
  /* Operation mode */

  public setOperationModeToModify(refId: string, applyOnAllLegs: boolean = false): void {
    this.setOperationMode(refId, OENOperationMode.MODIFY, applyOnAllLegs);
  }

  public setOperationModeToModifyInfo(refId: string, applyOnAllLegs: boolean = false): void {
    this.setOperationMode(refId, OENOperationMode.MODIFY_INFO, applyOnAllLegs);
  }

  public setOperationModeToNew(refId: string, applyOnAllLegs: boolean = false): void {
    this.setOperationMode(refId, OENOperationMode.NEW, applyOnAllLegs);
  }

  public setOperationModeToDuplicate(refId: string, applyOnAllLegs: boolean = false): void {
    this.setOperationMode(refId, OENOperationMode.DUPLICATE, applyOnAllLegs);
  }

  public setOperationModeToAmendMarkup(refId: string, applyOnAllLegs: boolean = false): void {
    this.setOperationMode(refId, OENOperationMode.AMEND_MARKUP, applyOnAllLegs);
  }

  public setOperationModeToMonitorExecution(refId: string, applyOnAllLegs: boolean = false): void {
    this.setOperationMode(refId, OENOperationMode.MONITOR_EXECUTION, applyOnAllLegs);
  }

  public setOperationMode(
    refId: string,
    mode: OENOperationMode,
    applyOnAllLegs: boolean = false,
  ): void {
    if (this.master.has(refId)) {
      this.master.get(refId).operationMode = mode;
      if (this.master.hasStrategy(refId) && applyOnAllLegs) {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        propagationTree.forEach((itemRefId: string) => {
          this.master.get(itemRefId).operationMode = mode;
        });
      }
    }
  }

  public setEntryType(refId: string, mode: OENEntryType): void {
    if (this.master.has(refId)) {
      this.master.get(refId).entryType = mode;
      if (this.master.hasStrategy(refId)) {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        propagationTree.forEach((itemRefId: string) => {
          this.master.get(itemRefId).entryType = mode;
        });
      }
    }
  }

  /**********************************************************************/
  /* Modification Lock */

  public setLockedForModification(refId: string, lockUserId: string): void {
    if (this.master.has(refId)) {
      let model = this.master.get(refId);
      model.isLockedForModification = true;
      model.lockUserId = lockUserId;
      if (this.master.hasStrategy(refId)) {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        //logger.out.info("DEV-TEST: Model LOCKED for modification: ", propagationTree.toString());
        propagationTree.forEach((itemRefId: string) => {
          let model = this.master.get(itemRefId);
          if (model && !model.isLockedForModification) {
            model.isLockedForModification = true;
            model.lockUserId = lockUserId;
          }
        });
      }
    }
  }

  public removeLockForModification(refId: string): void {
    if (this.master.has(refId)) {
      //logger.out.info("DEV-TEST: Model UNLOCKED for modification: ", refId);
      let model = this.master.get(refId);
      model.isLockedForModification = false;
      model.lockUserId = null;
      if (this.master.hasStrategy(refId)) {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        propagationTree.forEach((itemRefId: string) => {
          let model = this.master.get(itemRefId);
          if (model && model.isLockedForModification) {
            model.isLockedForModification = false;
            model.lockUserId = null;
          }
        });
      }
    }
  }

  /**********************************************************************/
  /* Modification Privatized */

  public setModificationPrivatized(refId: string): void {
    if (this.master.has(refId)) {
      this.master.get(refId).isModificationPrivatized = true;
      if (this.master.hasStrategy(refId)) {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        logger.out.debug(
          `DEBUG: _oeModel: Model PRIVATIZED for tree: ${propagationTree.toString()} `,
        );
        propagationTree.forEach((itemRefId: string) => {
          if (!this.master.get(itemRefId).isModificationPrivatized) {
            this.master.get(itemRefId).isModificationPrivatized = true;
          }
          this.master.get(itemRefId).isModificationClientCommentsPrivatized =
            this.settings.privatizeClientComments;
          this.master.get(itemRefId).isModificationPrivateCommentsPrivatized =
            this.settings.privatizePrivateComments;
        });
      }
    }
  }

  public removeModificationPrivatized(refId: string): void {
    if (this.master.has(refId)) {
      this.master.get(refId).isModificationPrivatized = false;
      if (this.master.hasStrategy(refId)) {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        logger.out.debug(
          `DEBUG: _oeModel: Model NOT-PRIVATIZED for tree: ${propagationTree.toString()} `,
        );
        propagationTree.forEach((itemRefId: string) => {
          if (this.master.get(itemRefId).isModificationPrivatized) {
            this.master.get(itemRefId).isModificationPrivatized = false;
          }

          this.master.get(itemRefId).isModificationClientCommentsPrivatized = false;
          this.master.get(itemRefId).isModificationPrivateCommentsPrivatized = false;
        });
      }
    }
  }

  /**********************************************************************/
  /* Validation errors  */

  public reportValidationErrors(refId: string): void {
    if (this.master.has(refId)) {
      this.master.get(refId).hasValidationErrors = true;
    }
  }

  public resetValidationErrors(refId: string): void {
    if (this.master.has(refId)) {
      this.master.get(refId).hasValidationErrors = false;
    }
  }

  public reportValidationErrorsOnMultipleRefIds(refIds: Array<string>): void {
    if (refIds != null && refIds instanceof Array && refIds.length > 0) {
      refIds.forEach((refId: string) => {
        this.reportValidationErrors(refId);
      });
    }
  }

  /**********************************************************************/
  /*  Tolerance Accept   */

  public setToleranceAccept(refId: string, value: boolean): void {
    if (this.master.has(refId)) {
      let rootRefId = this.getter.getRootIdForNodeWithRefId(refId);
      if (refId === rootRefId || this.master.has(rootRefId)) {
        this.master.get(rootRefId).toleranceAccept = value;
      }
    }
  }

  /**********************************************************************/
  /*  Tolerance Amount Accept   */

  public setToleranceAmountAccept(refId: string, value: boolean): void {
    if (this.master.has(refId)) {
      let rootRefId = this.getter.getRootIdForNodeWithRefId(refId);
      if (refId === rootRefId || this.master.has(rootRefId)) {
        this.master.get(rootRefId).toleranceAmountAccept = value;
      }
    }
  }

  public setNegativeMarkupAccept(refId: string, value: boolean): void {
    if (this.master.has(refId)) {
      let rootRefId = this.getter.getRootIdForNodeWithRefId(refId);
      if (refId === rootRefId || this.master.has(rootRefId)) {
        this.master.get(rootRefId).negativeMarkupAccept = value;
      }
    }
  }

  public setBMZeroAmountAccept(refId: string, value: boolean): void {
    if (this.master.has(refId)) {
      let rootRefId = this.getter.getRootIdForNodeWithRefId(refId);
      if (refId === rootRefId || this.master.has(rootRefId)) {
        this.master.get(rootRefId).bmZeroAmountAccept = value;
      }
    }
  }

  /**********************************************************************/
  /* MODEL SETTERS */
  public setClient(refId: string, _clientData: CubiqTS.ClientImpl | null): void {
    if (this.master.has(refId)) {
      //if(this.master.get(refId).strategy.isRoot){ // not needed atm
      let propagationTree = this.getter.getStrategyNodesListRoot(refId);
      propagationTree.forEach((itemRefId: string) => {
        if (this.master.get(itemRefId).client !== _clientData)
          this.master.get(itemRefId).client = _clientData;
      });
    } else {
      throw new Error('Client not updated! Model ref: "' + refId + '" not found.');
    }
  }

  public setAccount(refId: string, _accountData: CubiqTS.AccountImpl): void {
    if (this.master.has(refId)) {
      let propagationTree = this.getter.getStrategyNodesListRoot(refId);
      propagationTree.forEach((itemRefId: string) => {
        if (this.master.get(itemRefId).account !== _accountData) {
          this.master.get(itemRefId).account = _accountData;
        }
      });
    } else {
      throw new Error('Account not updated! Model ref: "' + refId + '" not found.');
    }
  }

  public setAllocations(refId: string, _allocations): void {
    if (this.master.has(refId)) {
      let propagationTree = this.getter.getStrategyNodesListRoot(refId);
      propagationTree.forEach((itemRefId: string) => {
        const model = this.master.get(itemRefId);
        if (model.allocations !== _allocations) {
          model.allocations = _allocations;
        }
      });
    } else {
      throw new Error('Allocations not updated! Model ref: "' + refId + '" not found.');
    }
  }

  public setFarAllocations(refId: string, _farAllocations): void {
    if (this.master.has(refId)) {
      let propagationTree = this.getter.getStrategyNodesListRoot(refId);
      propagationTree.forEach((itemRefId: string) => {
        const model = this.master.get(itemRefId);
        if (model.farAllocations !== _farAllocations) {
          model.farAllocations = _farAllocations;
        }
      });
    } else {
      throw new Error('Far Allocations not updated! Model ref: "' + refId + '" not found.');
    }
  }
  public setBulking(refId: string, bulking): void {
    if (this.master.has(refId)) {
      let propagationTree = this.getter.getStrategyNodesListRoot(refId);
      propagationTree.forEach((itemRefId: string) => {
        const model = this.master.get(itemRefId);
        if (model.bulking !== bulking) {
          model.bulking = bulking;
        }
      });
    } else {
      throw new Error('Allocations not updated! Model ref: "' + refId + '" not found.');
    }
  }

  public setBulked(refId: string, bulked): void {
    if (this.master.has(refId)) {
      let propagationTree = this.getter.getStrategyNodesListRoot(refId);
      propagationTree.forEach((itemRefId: string) => {
        const model = this.master.get(itemRefId);
        if (model.bulked !== bulked) {
          model.bulked = bulked;
        }
      });
    } else {
      throw new Error('Bulked not updated! Model ref: "' + refId + '" not found.');
    }
  }

  setProduct(refId: string, _product: any) {
    if (this.master.has(refId)) {
      let propagationTree = this.getter.getStrategyNodesListRoot(refId);
      propagationTree.forEach((itemRefId: string) => {
        const model = this.master.get(itemRefId);
        if (model.product !== _product) {
          model.product = _product;
        }
      });
    } else {
      throw new Error('Product not updated! Model ref: "' + refId + '" not found.');
    }
  }

  setSwapFixedAmount(refId: string, _swapFixedAmount: any) {
    if (this.master.has(refId)) {
      let propagationTree = this.getter.getStrategyNodesListRoot(refId);
      propagationTree.forEach((itemRefId: string) => {
        const model = this.master.get(itemRefId);
        if (model.swapFixedAmount !== _swapFixedAmount) {
          model.swapFixedAmount = _swapFixedAmount;
        }
      });
    } else {
      throw new Error('SwapFixedAmount not updated! Model ref: "' + refId + '" not found.');
    }
  }

  /** warning- for bulking service result pair with value date */
  setTenor(refId: string, _tenor: any) {
    if (this.master.has(refId)) {
      let propagationTree = this.getter.getStrategyNodesListRoot(refId);
      propagationTree.forEach((itemRefId: string) => {
        const model = this.master.get(itemRefId);
        if (model.valueDate.tenor !== _tenor) {
          model.valueDate.tenor = _tenor;
        }
      });
    } else {
      throw new Error('SwapTenor not updated! Model ref: "' + refId + '" not found.');
    }
  }

  setSwapTenor(refId: string, _swapTenor: any) {
    if (this.master.has(refId)) {
      let propagationTree = this.getter.getStrategyNodesListRoot(refId);
      propagationTree.forEach((itemRefId: string) => {
        const model = this.master.get(itemRefId);
        if (model.swapTenor !== _swapTenor) {
          model.swapTenor = _swapTenor;
        }
      });
    } else {
      throw new Error('SwapTenor not updated! Model ref: "' + refId + '" not found.');
    }
  }

  setSwapValueDate(refId: string, _swapValueDate: any) {
    if (this.master.has(refId)) {
      let propagationTree = this.getter.getStrategyNodesListRoot(refId);
      propagationTree.forEach((itemRefId: string) => {
        const model = this.master.get(itemRefId);
        if (model.swapValueDate !== _swapValueDate) {
          model.swapValueDate = _swapValueDate;
        }
      });
    } else {
      throw new Error('SwapValueDate not updated! Model ref: "' + refId + '" not found.');
    }
  }

  public setClientTrader(refId: string, _clientTrader: ICAutocompleteGenericModel): void {
    if (this.master.has(refId)) {
      let propagationTree = this.getter.getStrategyNodesListRoot(refId);
      propagationTree.forEach((itemRefId: string) => {
        if (!this.utility.compareObjects(this.master.get(itemRefId).clientTrader, _clientTrader)) {
          this.master.get(itemRefId).clientTrader = _clientTrader;
        }
      });
    } else {
      throw new Error('Client trader not updated! Model ref: "' + refId + '" not found.');
    }
  }

  public setContacts(refId: string, _contacts: any): void {
    if (this.master.has(refId)) {
      let propagationTree = this.getter.getStrategyNodesListRoot(refId);
      propagationTree.forEach((itemRefId: string) => {
        if (this.master.get(itemRefId).contacts !== _contacts) {
          this.master.get(itemRefId).contacts = _contacts;
        }
      });
    } else {
      throw new Error('Contacts not updated! Model ref: "' + refId + '" not found.');
    }
  }

  /* Financials */
  public setCcyPair(
    refId: string,
    _ccyPair: string,
    _ccyPairModel: CubiqTS.CurrencyPairImpl,
    propagateToAllNodes: boolean = true,
  ): void {
    if (this.master.has(refId)) {
      if (propagateToAllNodes) {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        propagationTree.forEach((itemRefId: string) => {
          if (this.master.get(itemRefId).ccyPair !== _ccyPair) {
            this.master.get(itemRefId).ccyPair = _ccyPair;
            this.master.get(itemRefId).ccyPairModel = _ccyPairModel;
          }
        });
      } else {
        if (this.master.get(refId).ccyPair !== _ccyPair) {
          this.master.get(refId).ccyPair = _ccyPair;
          this.master.get(refId).ccyPairModel = _ccyPairModel;
        }
      }
    } else {
      throw new Error('Ccy-pair not updated! Model ref: "' + refId + '" not found.');
    }
  }

  public setOrderType(refId: string, _orderType: ICAutocompleteGenericModel): void {
    if (this.master.has(refId)) {
      if (
        _orderType instanceof ICAutocompleteGenericModel &&
        _orderType.value !== CubiqTS.RequestOrderType.ALGO &&
        (this.getter.isEntryTypeAlgo(refId) || this.getter.isEntryTypeNdfAlgo(refId))
      ) {
        logger.out.error(
          `Trying to set '${
            _orderType != null ? CubiqTS.RequestOrderType[_orderType.value] : "NULL"
          }' in ALGO entry window! Operation ABORT!`,
        );
      } else {
        if (!this.utility.compareObjects(this.master.get(refId).orderType, _orderType)) {
          this.master.get(refId).orderType = _orderType;
        }
      }
    } else {
      throw new Error('Order-type not updated! Model ref: "' + refId + '" not found.');
    }
  }

  public setAlgoType(refId: string, _algoType: any): void {
    if (this.master.has(refId)) {
      if (!this.utility.equals(this.master.get(refId).algoType, _algoType)) {
        this.master.get(refId).algoType = _algoType;
        if (_algoType == null) {
          // reset algo parameters on null;
          this.master.get(refId).algoParameters = null;
        }
      }
    } else {
      throw new Error('Order-type not updated! Model ref: "' + refId + '" not found.');
    }
  }

  /*true = gamma
   */
  public setOrderAmount(refId: string, _orderAmount: CubiqTS.BigNumber): boolean {
    let isGamma: boolean = false;
    if (this.master.has(refId)) {
      isGamma = this.master.isGamma(refId);
      this.master.get(refId).orderAmount = _orderAmount;
    } else {
      throw new Error('Order-amount not updated! Model ref: "' + refId + '" not found.');
    }
    return isGamma;
  }

  public setClientIntent(refId: string, _clientIntent: CubiqTS.OrderIntent): boolean {
    if (!this.modelSync.isUpdateBlocked(refId)) {
      if (this.master.has(refId)) {
        if (this.master.get(refId).clientIntent !== _clientIntent) {
          this.master.get(refId).clientIntent = _clientIntent;
        }
        return true;
      } else {
        throw new Error('Client-intent not updated! Model ref: "' + refId + '" not found.');
      }
    } else {
      return false;
    }
  }

  public setClientActiveTab(refId: string, _clientActiveTab: string): void {
    if (this.master.has(refId)) {
      if (this.master.get(refId).clActiveTab !== _clientActiveTab) {
        this.master.get(refId).clActiveTab = _clientActiveTab;
      }
    } else {
      throw new Error('Client-active-tab not updated! Model ref: "' + refId + '" not found.');
    }
  }

  public setAccountActiveTab(refId: string, _accountActiveTab: string): void {
    if (this.master.has(refId)) {
      if (this.master.get(refId).accActiveTab !== _accountActiveTab) {
        this.master.get(refId).accActiveTab = _accountActiveTab;
      }
    } else {
      throw new Error('Account-active-tab not updated! Model ref: "' + refId + '" not found.');
    }
  }

  public setFixedCcy(
    refId: string,
    _fixedCcy: ICFixedCcyModel,
    propagateToAllNodes: boolean = true,
  ): void {
    if (!this.modelSync.isUpdateBlocked(refId)) {
      if (this.master.has(refId)) {
        if (propagateToAllNodes) {
          if (!this.utility.compareObjects(this.master.get(refId).fixedCcy, _fixedCcy)) {
            let propagationTree = this.getter.getStrategyNodesListRoot(refId);
            propagationTree.forEach((itemRefId: string) => {
              this.master.get(itemRefId).fixedCcy = <ICFixedCcyModel>_fixedCcy;
            });
          }
        } else {
          if (!this.utility.compareObjects(this.master.get(refId).fixedCcy, _fixedCcy)) {
            this.master.get(refId).fixedCcy = <ICFixedCcyModel>_fixedCcy;
          }
        }
      } else {
        throw new Error('Client-intent not updated! Model ref: "' + refId + '" not found.');
      }
    }
  }

  public setRequestPrice(refId: string, _requestPrice: CubiqTS.BigNumber): boolean {
    if (!this.modelSync.isUpdateBlocked(refId)) {
      if (this.master.has(refId)) {
        this.master.get(refId).requestPrice = _requestPrice;
        return true;
      } else {
        throw new Error('Request-price not updated! Model ref: "' + refId + '" not found.');
      }
    } else {
      return false;
    }
  }

  public setTrailingPips(refId: string, _trailingPips: CubiqTS.BigNumber): boolean {
    //if (!this.modelSync.isUpdateBlocked(refId)) {
    if (this.master.has(refId)) {
      this.master.get(refId).trailingPips = _trailingPips;
      return true;
    } else {
      throw new Error('Trailing Pips not updated! Model ref: "' + refId + '" not found.');
    }
    // } else {
    //   return false;
    // }
  }

  public setTrailingTrigger(refId: string, _trailingTrigger: CubiqTS.BigNumber): boolean {
    if (!this.modelSync.isUpdateBlocked(refId)) {
      if (this.master.has(refId)) {
        this.master.get(refId).trailingTrigger = _trailingTrigger;
        return true;
      } else {
        throw new Error('Trailing Trigger not updated! Model ref: "' + refId + '" not found.');
      }
    } else {
      return false;
    }
  }

  public setTrailingTriggerProvided(refId: string, _value: boolean): boolean {
    if (!this.modelSync.isUpdateBlocked(refId)) {
      if (this.master.has(refId)) {
        this.master.get(refId).trailingTriggerProvided = _value;
        return true;
      } else {
        throw new Error('Trailing Trigger not updated! Model ref: "' + refId + '" not found.');
      }
    } else {
      return false;
    }
  }

  public setLimitPrice(refId: string, value: any): boolean {
    if (!this.modelSync.isUpdateBlocked(refId)) {
      if (this.master.has(refId)) {
        this.master.get(refId).limitPrice = value;
        return true;
      } else {
        throw new Error('Limit-price not updated! Model ref: "' + refId + '" not found.');
      }
    } else {
      return false;
    }
  }

  public setFixing(
    refId: string,
    _fixCode: string,
    _fixTime: string,
    _fixTZ: string,
    forceUpdate = false,
  ): boolean {
    let restoreBlockUpdate = false;
    // temporary fix because of  https://www.bcdfx.com/ijira/browse/OMS-32837
    if (forceUpdate) {
      restoreBlockUpdate = this.modelSync.isUpdateBlocked(refId);
      this.modelSync.removeFromBlockUpdateList(refId);
    }
    if (!this.modelSync.isUpdateBlocked(refId)) {
      if (this.master.has(refId)) {
        let oedm: OEDataModel = this.master.get(refId);

        // NB TBD

        oedm.fixingCode = _fixCode;
        oedm.fixingTime = _fixTime;
        oedm.fixingTimezone = _fixTZ;
        if (restoreBlockUpdate) {
          this.modelSync.addToBlockUpdateList(refId);
        }
        return true;
      } else {
        throw new Error('BM Fixing Details  not updated! Model ref: "' + refId + '" not found.');
      }
    } else {
      return false;
    }
  }

  public setValueDate(refId: string, _valueDate: ICTenorValueDateModel): void {
    if (this.master.has(refId)) {
      let propagationTree = this.getter.getStrategyNodesListRoot(refId);
      propagationTree.forEach((itemRefId: string) => {
        if (this.master.get(itemRefId).valueDate !== _valueDate)
          this.master.get(itemRefId).valueDate = _valueDate;
      });
    } else {
      throw new Error('Value-date not updated! Model ref: "' + refId + '" not found.');
    }
  }

  public setMarkup(refId: string, _markup: ICMarkupModel): void {
    if (!this.modelSync.isUpdateBlocked(refId)) {
      if (this.master.has(refId)) {
        this.master.get(refId).markup = _markup;
      } else {
        throw new Error('Markup not updated! Model ref: "' + refId + '" not found.');
      }
    }
  }

  public setTraderGroup(refId: string, _traderGroup: CubiqTS.TradingGroupImpl): void {
    if (this.master.has(refId)) {
      if (this.getter.isNotInTerminalState(refId)) {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        propagationTree.forEach((itemRefId: string) => {
          if (
            !this.utility.compareObjects(this.master.get(itemRefId).traderGroup, _traderGroup) &&
            this.getter.isNotInTerminalState(itemRefId)
          ) {
            this.master.get(itemRefId).traderGroup = _traderGroup;
          }
        });
      }
    } else {
      throw new Error('Trader Group not updated! Model ref: "' + refId + '" not found.');
    }
  }

  public setTrigger(refId: string, _trigger: ICAutocompleteGenericModel): void {
    if (this.master.has(refId)) {
      if (this.getter.isNotInTerminalState(refId)) {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        propagationTree.forEach((itemRefId: string) => {
          if (
            !this.utility.compareObjects(this.master.get(itemRefId).trigger, _trigger) &&
            this.getter.isNotInTerminalState(itemRefId)
          ) {
            this.master.get(itemRefId).trigger = _trigger;
          }
        });
      }
    } else {
      throw new Error('Trigger not updated! Model ref: "' + refId + '" not found.');
    }
  }

  public setTraderGroupCodeId(refId: string, _traderGroupCode: string): void {
    if (this.master.has(refId)) {
      if (this.getter.isNotInTerminalState(refId)) {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        propagationTree.forEach((itemRefId: string) => {
          if (
            this.master.get(itemRefId).traderGroupCodeId !== _traderGroupCode &&
            this.getter.isNotInTerminalState(itemRefId)
          ) {
            this.master.get(itemRefId).traderGroupCodeId = _traderGroupCode;
          }
        });
      }
    }
  }

  public setSuspend(refId: string, _value: ICSuspendModel): void {
    if (this.master.has(refId)) {
      if (_value instanceof ICSuspendModel) {
        this.master.get(refId).suspend = new ICSuspendModel();
        this.master.get(refId).suspend.activated = _value.activated;
        this.master.get(refId).suspend.suspendFrom =
          _value.suspendFrom != null
            ? Object.assign(new ICTimeControlModel(), _value.suspendFrom)
            : null; //JSON.parse(JSON.stringify(_value.suspendFrom));
        this.master.get(refId).suspend.suspendTo =
          _value.suspendTo != null
            ? Object.assign(new ICTimeControlModel(), _value.suspendTo)
            : null; //JSON.parse(JSON.stringify(_value.suspendTo));
        if (!(this.master.get(refId).suspend instanceof ICSuspendModel)) {
          throw new Error(
            `Suspend updated but incorrect model assigned ${this.master.get(refId).suspend}`,
          );
        }
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        propagationTree.forEach((itemRefId: string) => {
          this.master.get(itemRefId).suspend = this.master.get(refId).suspend;
          this.master.get(itemRefId).suspend.activated = this.master.get(refId).suspend.activated;
          this.master.get(itemRefId).suspend.suspendFrom =
            this.master.get(refId).suspend.suspendFrom;
          this.master.get(itemRefId).suspend.suspendTo = this.master.get(refId).suspend.suspendTo;
        });
      } else {
        throw new Error(`Suspend not updated! value model is incorrect ${_value}`);
      }
    } else {
      throw new Error('Suspend not updated! Model ref: "' + refId + '" not found.');
    }
  }

  public setGoodFrom(refId: string, _value: ICGoodFromUntilModel): void {
    if (this.master.has(refId)) {
      if (this.getter.isNotInTerminalState(refId)) {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        propagationTree.forEach((itemRefId: string) => {
          if (
            this.master.get(itemRefId).goodFrom !== _value &&
            !this.master.get(itemRefId).goodFrom?.isLoadPending
          ) {
            this.master.get(itemRefId).goodFrom = _value;
          }
        });
      }
    } else {
      throw new Error('Good From not updated! Model ref: "' + refId + '" not found.');
    }
  }

  public setGoodUntil(refId: string, _value: ICGoodFromUntilModel): void {
    if (this.master.has(refId)) {
      if (this.getter.isNotInTerminalState(refId)) {
        if (this.master.get(refId).goodUntil?.isLoadPending) {
          return;
        }
        this.master.get(refId).goodUntil = _value;
        // sibling should share good until
        if (this.master.get(refId).strategy) {
          let siblings = this.master.get(refId).strategy.siblings;
          if (siblings) {
            siblings.forEach((sibling) => {
              this.master.get(sibling.refId).goodUntil = _value;
            });
          }
        }
      }
    } else {
      throw new Error('Good Until not updated! Model ref: "' + refId + '" not found.');
    }
  }

  public setFixingSource(refId: string, _fixingSource: ICFixingSourceModel): void {
    if (this.master.has(refId)) {
      if (this.getter.isNotInTerminalState(refId)) {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        propagationTree.forEach((itemRefId: string) => {
          if (
            !this.utility.compareObjects(this.master.get(itemRefId).fixingSource, _fixingSource) &&
            this.getter.isNotInTerminalState(itemRefId)
          ) {
            this.master.get(itemRefId).fixingSource = _fixingSource;
          }
        });
      }
    } else {
      throw new Error('Fixing Code not updated! Model ref: "' + refId + '" not found.');
    }
  }

  public setFixingCode(refId: string, _fixingCode: ICFixingSourceModel): void {
    if (this.master.has(refId)) {
      if (this.getter.isNotInTerminalState(refId)) {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        propagationTree.forEach((itemRefId: string) => {
          if (
            _fixingCode != null &&
            _fixingCode.value != null &&
            this.getter.isNotInTerminalState(itemRefId)
          ) {
            this.master.get(itemRefId).fixingCode = _fixingCode.value;
          }
        });
      }
    } else {
      throw new Error('Fixing Code not updated! Model ref: "' + refId + '" not found.');
    }
  }

  public setFixingDate(refId: string, _fixingDate: Date): void {
    if (this.master.has(refId)) {
      if (this.getter.isNotInTerminalState(refId)) {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        propagationTree.forEach((itemRefId: string) => {
          if (
            !this.utility.compareDates(this.master.get(itemRefId).fixingDate, _fixingDate) &&
            this.getter.isNotInTerminalState(itemRefId)
          ) {
            this.master.get(itemRefId).fixingDate = _fixingDate;
          }
        });
      }
    } else {
      throw new Error('Fixing Code not updated! Model ref: "' + refId + '" not found.');
    }
  }

  /**********************************************************************/
  /* More details */
  /**************************/
  /* Comments GENERAL  */

  public setDefaultComments(
    refId: string,
    _defaultClientComment: string,
    _defaultPrivateComment: string,
  ): void {
    if (this.master.has(refId)) {
      let propagationTree = this.getter.getStrategyNodesListRoot(refId);
      propagationTree.forEach((itemRefId: string) => {
        this.initializeClientCommentIfNull(itemRefId);
        this.master.get(itemRefId).clientComment.defaultComment = _defaultClientComment
          ? _defaultClientComment
          : null;
        this.master.get(itemRefId).privateComments = _defaultPrivateComment
          ? _defaultPrivateComment
          : null;
        if (!this.getter.isInTerminalState(itemRefId)) {
          //reset only if not a terminal leg
          this.master.get(itemRefId).clientComment.manualComment =
            _defaultClientComment != null ? _defaultClientComment : "";
        }
        this.updateClientCommentValue(itemRefId);
      });
    } else {
      throw new Error('Client Default comments not updated! Model ref: "' + refId + '" not found.');
    }
  }

  public setClientTraderClientComment(refId: string, _clientTraderClientComment: string): void {
    if (this.master.has(refId)) {
      let propagationTree = this.getter.getStrategyNodesListRoot(refId);
      propagationTree.forEach((itemRefId: string) => {
        this.initializeClientCommentIfNull(itemRefId);
        if (
          this.master.get(itemRefId).clientComment.clientTraderComment !==
          _clientTraderClientComment
        ) {
          this.master.get(itemRefId).clientComment.clientTraderComment = _clientTraderClientComment;
          /* if(!this.isInTerminalState(itemRefId) && _clientTraderClientComment != null){ //reset if  client trader comment exists and not a terminal leg
						this.master.get(itemRefId).clientComment.manualComment = null;
					} */
          this.updateClientCommentValue(itemRefId);
        }
      });
    } else {
      throw new Error(
        'Client ClientTrader Comment not updated! Model ref: "' + refId + '" not found.',
      );
    }
  }

  /**************************/
  /* Comments PRIVATE */

  public setPrivateComment(refId: string, _comment: string): void {
    if (this.settings.privateCommentsEnabled) {
      if (this.master.has(refId)) {
        if (this.settings.independentComments) {
          this.master.get(refId).privateComments = _comment;
        } else {
          let propagationTree = this.getter.getStrategyNodesListRoot(refId);
          propagationTree.forEach((itemRefId: string) => {
            this.master.get(itemRefId).privateComments = _comment;
          });
        }
      } else {
        throw new Error('Private comment not updated! Model ref: "' + refId + '" not found.');
      }
    }
  }

  /**************************/
  /* Comments CLIENT */

  public setManualClientComment(refId: string, _manualClientComment: string): void {
    if (this.master.has(refId)) {
      this.initializeClientCommentIfNull(refId);
      // remove client trader string
      let clientTraderComment = this.master.get(refId).clientComment.clientTraderComment;
      let parsedManualClientComment = this.utility.removeClientTraderCommentFromManualComment(
        _manualClientComment,
        clientTraderComment,
      );

      if (this.settings.independentComments) {
        this.master.get(refId).clientComment.manualComment = parsedManualClientComment;
        this.master.get(refId).clientComment.value = _manualClientComment;
      } else {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        propagationTree.forEach((itemRefId: string) => {
          this.master.get(itemRefId).clientComment.manualComment = parsedManualClientComment;
          this.master.get(refId).clientComment.value = _manualClientComment;
        });
      }
    } else {
      throw new Error('Client Manual Comment not updated! Model ref: "' + refId + '" not found.');
    }
  }

  private initializeClientCommentIfNull(refId: string): void {
    if (this.master.get(refId).clientComment == null) {
      this.master.get(refId).clientComment = new ICCommentsModel();
    }
  }

  /**
   *  invoked in every set<>Comment() method in order to maintain the  BfxOrderEntryCommentsModel.value up to date for model access
   */
  public updateClientCommentValue(refId: string): void {
    if (this.master.has(refId)) {
      this.initializeClientCommentIfNull(refId);
      if (!this.getter.isInTerminalState(refId)) {
        let clientTraderComment = this.master.get(refId).clientComment.clientTraderComment;
        let clientManualComment = this.master.get(refId).clientComment.manualComment;
        let clientValueComment = this.master.get(refId).clientComment.value;

        if (
          clientValueComment == null ||
          (clientTraderComment == null && clientValueComment !== clientManualComment) ||
          (clientTraderComment != null &&
            clientValueComment !== clientManualComment + "; " + clientTraderComment)
        ) {
          let comment: string = null;
          //if(clientManualComment != null){ //  && this.master.get(refId).clientComment.manualComment.length > 0
          comment = clientManualComment != null ? clientManualComment : "";
          /* 	}else if(this.master.get(refId).clientComment.defaultComment != null && this.master.get(refId).clientComment.defaultComment.length > 0){
						comment = this.master.get(refId).clientComment.defaultComment;
					} */ /* else{
						comment = '';
					} */
          if (clientTraderComment != null) {
            let alreadyTerminated = comment.endsWith("; "); //comment.indexOf('; ') == comment.length -2;
            comment = comment
              .concat(comment.length > 0 && !alreadyTerminated ? "; " : "")
              .concat(clientTraderComment);
          }
          //comment = comment !== '' ?  comment : null;
          this.master.get(refId).clientComment.value = comment;
        }
      }
    } else {
      throw new Error(`Can't get Client Comment! Model ref: "${refId}" not found.`);
    }
  }

  public getClientComment(refId: string): string {
    try {
      //this.updateClientCommentValue(refId);
      let comment = this.master.get(refId).clientComment.value;
      return (comment = comment != null ? comment : "");
    } catch (ex) {
      logger.out.error(`EXCEPTION: _oeModel.getClientComment():  %O`, ex);
      return "";
    }
  }
  /* Client Comments -END */
  /**************************/

  public setHoldSTP(refId: string, _value: boolean): void {
    if (!this.modelSync.isUpdateBlocked(refId)) {
      if (this.master.has(refId)) {
        if (this.master.isBenchmarkRequest(refId)) {
          this.master.get(refId).holdSTP = _value;
        } else {
          let propagationTree = this.getter.getStrategyNodesListRoot(refId);
          propagationTree.forEach((itemRefId: string) => {
            this.master.get(itemRefId).holdSTP = _value;
            //logger.out.info(`HSTP-DEBUG: _oeModel.setHoldSTP(): SET refId (${itemRefId}) value: ${_value}`);
          });
          /* setTimeout(()=>{
						logger.out.info(`HSTP-DEBUG: _oeModel.setHoldSTP(): AFTER-TIMEOUT refId (${refId}) value: ${this.master.get(refId).holdSTP}`);
					}, 300); */
        }
      } else {
        throw new Error('Hold STP not updated! Model ref: "' + refId + '" not found.');
      }
    }
  }

  public setAutoForwardPrice(refId: string, _value: boolean): void {
    if (!this.modelSync.isUpdateBlocked(refId)) {
      if (this.master.has(refId)) {
        if (this.master.isBenchmarkRequest(refId) || this.master.isALGORequest(refId)) {
          this.master.get(refId).autoForwardPrice = _value;
        } else {
          // CAn be called on init when model is blank so no eror logging
          // logger.out.error(`autoForwardPrice not suported`);
        }
      } else {
        throw new Error('Auto Forward Price not updated! Model ref: "' + refId + '" not found.');
      }
    }
  }

  public setPrivateOrder(refId: string, _value: boolean): void {
    if (!this.modelSync.isUpdateBlocked(refId)) {
      if (this.master.has(refId)) {
        if (this.master.isBenchmarkRequest(refId)) {
          this.master.get(refId).privateOrder = _value;
        } else {
          let propagationTree = this.getter.getStrategyNodesListRoot(refId);
          propagationTree.forEach((itemRefId: string) => {
            this.master.get(itemRefId).privateOrder = _value;
          });
        }
      } else {
        throw new Error('Private Order not updated! Model ref: "' + refId + '" not found.');
      }
    }
  }

  public setExecutionMode(refId: string, _value: CubiqTS.ExecutionType): boolean {
    if (!this.modelSync.isUpdateBlocked(refId)) {
      if (this.master.has(refId)) {
        let propagationTree = this.getter.getStrategyNodesListRoot(refId);
        propagationTree.forEach((itemRefId: string) => {
          this.master.get(itemRefId).executionMode = _value;
        });
        return true;
      } else {
        throw new Error('Execution Mode not updated! Model ref: "' + refId + '" not found.');
      }
    } else {
      return false;
    }
  }

  public setAllowPartialFills(refId: string, _allowPartialFills: ConstraintValue): void {
    if (this.master.has(refId)) {
      this.master.get(refId).allowPartialFills = _allowPartialFills;
    } else {
      throw new Error('Allow-partial-fills not updated! Model ref: "' + refId + '" not found.');
    }
  }

  public setAllowSTPAgg(refId: string, _allowSTPAgg: ConstraintValue): void {
    if (this.master.has(refId)) {
      this.master.get(refId).stpAggregationFlag = _allowSTPAgg;
    } else {
      throw new Error('Allow-STPAgg not updated! Model ref: "' + refId + '" not found.');
    }
  }

  /**********************************************************************/
  /* Algo Strategy Panel */

  public resetAlgoParameters(refId: string) {
    if (this.master.has(refId)) {
      setTimeout(() => {
        let algoType = this.master.get(refId).algoType;
        if (
          (this.getter.isEntryTypeAlgo(refId) || this.getter.isEntryTypeNdfAlgo(refId)) &&
          (algoType == null ||
            (algoType instanceof ICAutocompleteGenericModel && algoType.value == null))
        ) {
          this.master.get(refId).algoParameters = null;
        }
      }, 100);
    } else {
      throw new Error(`Algo Strategy Parameters not set! Model ref: '${refId}' not found.`);
    }
  }

  public setAlgoParameters(
    refId: string,
    _algoId: string,
    _algoParameters: Array<ATDLParameterDataModel>,
  ): void {
    if (this.master.has(refId)) {
      if (
        (this.getter.isEntryTypeAlgo(refId) || this.getter.isEntryTypeNdfAlgo(refId)) &&
        this.master.get(refId).algoType instanceof ICAutocompleteGenericModel &&
        this.master.get(refId).algoType.value === _algoId
      ) {
        let modelParameters = this.master.get(refId).algoParameters;
        if (
          _algoParameters instanceof Array &&
          !(
            modelParameters instanceof Array &&
            modelParameters.length > 0 &&
            modelParameters.length === _algoParameters.length &&
            modelParameters[0].algoId === _algoId
          )
        ) {
          this.master.get(refId).algoParameters = _algoParameters;
        } else {
          logger.out.info(`parameters for ${_algoId} already set!`);
        }
      } else {
        throw new Error(
          `Trying to set Algo Parameters for invalid AlgoID! model: ${
            this.master.get(refId).algoType
          } | params: ${_algoId}`,
        );
      }
    } else {
      throw new Error(`Algo Strategy Parameters not set! Model ref: '${refId}' not found.`);
    }
  }

  public setAlgoParameter(
    refId: string,
    _algoId: string,
    _algoParameter: ATDLParameterDataModel,
  ): void {
    if (this.master.has(refId)) {
      if (
        (this.getter.isEntryTypeAlgo(refId) || this.getter.isEntryTypeNdfAlgo(refId)) &&
        this.master.get(refId).algoType instanceof ICAutocompleteGenericModel &&
        this.master.get(refId).algoType.value === _algoId
      ) {
        if (
          _algoParameter instanceof ATDLParameterDataModel &&
          this.master.get(refId).algoParameters instanceof Array
        ) {
          let index = this.master
            .get(refId)
            .algoParameters.map((item) => item.refName)
            .indexOf(_algoParameter.refName);
          if (index >= 0) {
            this.master.get(refId).algoParameters[index] = _algoParameter;
          } else {
            throw new Error(`Algo Parameter '${_algoParameter.refName}' not found.`);
          }
        } else {
          throw new Error(`Invalid Algo Parameter argument or data model.`);
        }
      } else {
        throw new Error(
          `Trying to set Algo Parameter for invalid AlgoID! model: ${
            this.master.get(refId).algoType
          } | argument: ${_algoId} .`,
        );
      }
    } else {
      throw new Error(`Algo Parameter not set! Model ref: '${refId}' not found.`);
    }
  }

  public setAlgoParameterValue(
    refId: string,
    _algoId: string,
    _algoParameterName: string,
    _algoParameterValue: any,
  ): void {
    if (this.master.has(refId)) {
      if (
        (this.getter.isEntryTypeAlgo(refId) || this.getter.isEntryTypeNdfAlgo(refId)) &&
        this.master.get(refId).algoType instanceof ICAutocompleteGenericModel &&
        this.master.get(refId).algoType.value === _algoId
      ) {
        if (
          typeof _algoParameterName === "string" &&
          this.master.get(refId).algoParameters instanceof Array
        ) {
          let index = this.master
            .get(refId)
            .algoParameters.map((item) => item.refName)
            .indexOf(_algoParameterName);
          if (
            index >= 0 &&
            this.master.get(refId).algoParameters[index] instanceof ATDLParameterDataModel
          ) {
            this.master.get(refId).algoParameters[index].value = _algoParameterValue;
          } else {
            throw new Error(`Algo Parameter '${_algoParameterName}' not found`);
          }
        } else {
          throw new Error(
            `Invalid Algo Parameter name argument ('${_algoParameterName}') or data model.`,
          );
        }
      } else {
        throw new Error(
          `Trying to update Algo Parameter for invalid AlgoID! model: ${
            this.master.get(refId).algoType
          } | argument: ${_algoId}`,
        );
      }
    } else {
      throw new Error(`Algo Parameter value not updated! Model ref: '${refId}' not found.`);
    }
  }

  public setOBOClient(refId: string, value: string): void {
    if (!this.modelSync.isUpdateBlocked(refId)) {
      if (this.master.has(refId)) {
        if (this.master.isBenchmarkRequest(refId)) {
          this.master.get(refId).oboClient = value;
        } else {
          let propagationTree = this.getter.getStrategyNodesListRoot(refId);
          propagationTree.forEach((itemRefId: string) => {
            this.master.get(itemRefId).oboClient = value;
          });
        }
      } else {
        throw new Error('OBO Client not updated! Model ref: "' + refId + '" not found.');
      }
    }
  }

  public setOBOAccount(refId: string, value: string): void {
    if (!this.modelSync.isUpdateBlocked(refId)) {
      if (this.master.has(refId)) {
        if (this.master.isBenchmarkRequest(refId)) {
          this.master.get(refId).oboAccount = value;
        } else {
          let propagationTree = this.getter.getStrategyNodesListRoot(refId);
          propagationTree.forEach((itemRefId: string) => {
            this.master.get(itemRefId).oboAccount = value;
          });
        }
      } else {
        throw new Error('OBO Account not updated! Model ref: "' + refId + '" not found.');
      }
    }
  }

  public setOBOUser(refId: string, value: string): void {
    if (!this.modelSync.isUpdateBlocked(refId)) {
      if (this.master.has(refId)) {
        if (this.master.isBenchmarkRequest(refId)) {
          this.master.get(refId).oboUser = value;
        } else {
          let propagationTree = this.getter.getStrategyNodesListRoot(refId);
          propagationTree.forEach((itemRefId: string) => {
            this.master.get(itemRefId).oboUser = value;
          });
        }
      } else {
        throw new Error('OBO User not updated! Model ref: "' + refId + '" not found.');
      }
    }
  }

  public setDelta(refId: string, value: string): void {
    if (!this.modelSync.isUpdateBlocked(refId)) {
      if (this.master.has(refId)) {
        this.master.get(refId).delta = value;
      } else {
        throw new Error('Gamma delta not updated! Model ref: "' + refId + '" not found.');
      }
    }
  }

  public setRungsAbove(refId: string, value: number): void {
    if (!this.modelSync.isUpdateBlocked(refId)) {
      if (this.master.has(refId)) {
        this.master.get(refId).rungsAbove = value;
      } else {
        throw new Error('Gamma rungs above not updated! Model ref: "' + refId + '" not found.');
      }
    }
  }

  public setRungsBelow(refId: string, value: number): void {
    if (!this.modelSync.isUpdateBlocked(refId)) {
      if (this.master.has(refId)) {
        this.master.get(refId).rungsBelow = value;
      } else {
        throw new Error('Gamma rungs below not updated! Model ref: "' + refId + '" not found.');
      }
    }
  }

  // Custom Fields
  public setCustomValues(refId: string, _value: Array<CubiqTS.CustomValue>): void {
    if (this.master.has(refId)) {
      let propagationTree = this.getter.getStrategyNodesListRoot(refId);
      propagationTree.forEach((itemRefId: string) => {
        this.master.get(itemRefId).customValues = _value;
      });
    } else {
      throw new Error('Custom Fields not updated! Model ref: "' + refId + '" not found.');
    }
  }

  public setGammaModel(refId: string, gammaLadderModel: OEPanelGammaViewModel) {
    if (this.master.has(refId)) {
      this.master.get(refId).gammaModel = gammaLadderModel;
    }
  }
}
